import React, { useEffect, useState } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import _ from "lodash";
import { format, isAfter, isBefore, isSameDay } from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import toast from "react-hot-toast";

// constants
import LocalStrings from "@language";
import { PROPOSAL_PORTAL_URL, CDN_BASE_IMAGES_URL } from "@constant";
import { LOCAL_TIMEZONE } from "@datehandle";
import {
  formUpFullName,
  isJSONParsable,
  calculateAverageScore,
} from "@utilscommon";
import {
  REVIEW_ALLOWANCES,
  PROPOSAL_SUBMISSION_TYPES,
  REVIEWER_RESPONSE_TYPES,
} from "@reviewerconstant";

// actions
import { setCommonModal } from "@mainaction";
import {
  rejectProposal,
  addProposalReview,
  markResponseReviewed,
} from "@revieweractions";

// components
import {
  ToggleSlider,
  renderFieldTextarea,
  renderSpinner,
} from "@basecomponent";
import CommonModal from "@commonmodal";

// files
import CrossIcon from "../../../assets/icons/x-close.svg";
import StarIcon from "../../../assets/icons/Star";
import StarOutlinedIcon from "../../../assets/icons/StarOutlined";

const LOADER_TYPES = ["reject", "resubmit", "submit"];
const LOADER_INIT = { state: false, type: LOADER_TYPES[2] }; // types => reject, resubmit, submit

const ProposalReview = (props) => {
  const {
    // state
    session,
    eventData,
    interestList,
    commonModal,
    criteriaList,
    criteriaRankList,
    allowedToReview,
    isReviewLayout,

    // component
    listRefresh = () => {},
  } = props;

  const [loader, setLoader] = useState(LOADER_INIT);
  const [timeConversionToggle, setTimeConversionToggle] = useState(false);
  const [reviewerComment, setReviewerComment] = useState("");
  const [ratedStars, setRatedStars] = useState([]); // { criteriaId, criteriaName, max, value }

  const proposalUrl = !_.isNil(commonModal?.data?.proposalidfk)
    ? `${PROPOSAL_PORTAL_URL}/${commonModal?.data?.proposalidfk}`
    : "";

  useEffect(() => {
    if (!_.isEmpty(criteriaRankList)) {
      _.map(criteriaRankList, (criteria) => {
        setRatedStars((prev) => {
          return [
            ...prev,
            {
              criteriaId: criteria?.criteriaidfk,
              criteriaName: criteria?.criterianame,
              max: 0,
              value: criteria?.rating,
            },
          ];
        });
      });
    }
  }, [criteriaRankList]);

  const handleCloseModal = () => {
    props.setCommonModal();
  };

  // For reject and mark-incomplete case
  const handleReviewRejection = (type) => {
    if (
      !_.isEqual(REVIEW_ALLOWANCES.allowed, allowedToReview) ||
      _.isEmpty(type) ||
      !_.includes(PROPOSAL_SUBMISSION_TYPES.types, type) ||
      (_.includes([LOADER_TYPES[0], LOADER_TYPES[1]], loader?.type) &&
        loader.state)
    )
      return;

    setLoader({ type, state: true });
    const proposalDetails = commonModal?.data;
    props.rejectProposal(
      session,
      {
        responseId: proposalDetails?.id,
        comment: reviewerComment,
        type,
      },
      (res) => {
        setLoader(LOADER_INIT);
        if (res?.success && listRefresh) listRefresh();
        handleCloseModal();
      }
    );
  };

  const handleResponseSubmission = () => {
    if (
      !_.isEqual(REVIEW_ALLOWANCES.allowed, allowedToReview) ||
      (_.isEqual(loader?.type, LOADER_TYPES[2]) && loader.state)
    )
      return;
    if (!_.isEqual(criteriaList?.length, ratedStars?.length)) {
      toast.error(LocalStrings.static.kCriteriaRankMissed);
      return;
    }

    const proposalDetails = commonModal?.data;
    const proposalReviewPayload = _.map(ratedStars, (item) => {
      return {
        criteriaidfk: item?.criteriaId,
        criterianame: item?.criteriaName,
        rating: item?.value,
        responseidfk: proposalDetails?.id,
        eventid: session?.eventid,
      };
    });
    const obtainedRanks = _.map(ratedStars, (item) => {
      return [item?.value, item?.max];
    });
    const avgScore = calculateAverageScore(obtainedRanks);

    setLoader({ type: LOADER_INIT[2], state: true });
    props.addProposalReview(session, proposalReviewPayload, (reviewRes) => {
      if (reviewRes?.success) {
        props.markResponseReviewed(
          session,
          {
            responseId: proposalDetails?.id,
            avgRating: avgScore,
            comment: reviewerComment,
          },
          (res) => {
            setLoader(LOADER_INIT);
            if (res?.success && listRefresh) listRefresh();
            handleCloseModal();
          }
        );
      } else setLoader(LOADER_INIT);
    });
  };

  const _renderAbstractDetails = () => {
    const proposalDetails = commonModal?.data;
    if (_.isEmpty(proposalDetails)) return <></>;

    const submissionDate = proposalDetails?.submission_date;
    const submissionDateLocal = !_.isEmpty(submissionDate)
      ? `${submissionDate.split(" ").join("T")}Z`
      : "";
    const zonedTime = utcToZonedTime(
      submissionDateLocal,
      timeConversionToggle ? eventData?.eventtimezone : LOCAL_TIMEZONE
    ); // utc to event/client timezone
    const formattedZonedTime = format(zonedTime, "d MMM, yyyy HH:mm a");

    const handleUserWantsConversionChanged = () => {
      setTimeConversionToggle(!timeConversionToggle);
    };

    return (
      <div className="flex flex-col w-full border rounded">
        {/* --- Proposal name --- */}
        <div className="grid grid-cols-2 w-full border-b">
          <div className="col-span-1 px-2 py-1 text-sm font-medium">
            {LocalStrings.static.kAbstractName}
          </div>
          <div className="col-span-1 px-2 py-1 text-sm">
            {!_.isEmpty(
              proposalDetails?.proposal_by_proposalidfk?.proposal_name
            )
              ? proposalDetails?.proposal_by_proposalidfk?.proposal_name
              : ""}
          </div>
        </div>

        {/* --- Submitted by --- */}
        <div className="grid grid-cols-2 w-full border-b">
          <div className="col-span-1 px-2 py-1 text-sm font-medium">
            {LocalStrings.static.kSubmittedBy}
          </div>
          <div className="col-span-1 px-2 py-1 text-sm">
            {formUpFullName(
              proposalDetails?.firstname,
              proposalDetails?.lastname
            )}
          </div>
        </div>

        {/* --- Submission time --- */}
        <div className="grid grid-cols-2 w-full">
          <div className="col-span-1 px-2 py-1 text-sm font-medium">
            {LocalStrings.static.kSubmissionTime}
          </div>
          <div className="col-span-1 px-2 py-1 text-sm h-full flex justify-between items-center">
            <span>{formattedZonedTime}</span>
            <div className="flex gap-2 items-center h-full">
              <span>( {eventData?.eventtimezone}</span>
              <ToggleSlider
                enabled={timeConversionToggle}
                setEnabled={handleUserWantsConversionChanged}
              />
              )
            </div>
          </div>
        </div>
      </div>
    );
  };

  const _renderProposalResponse = () => {
    const proposalDetails = commonModal?.data;
    if (_.isEmpty(proposalDetails)) return <></>;

    const proposalResponse = isJSONParsable(proposalDetails?.response_JSON)
      ? JSON.parse(proposalDetails?.response_JSON)
      : {};
    if (_.isEmpty(proposalResponse)) return <></>;

    const handleInterestValue = (value) => {
      return !_.isEmpty(interestList?.data)
        ? _.chain(interestList?.data)
            .filter((interest) => _.includes(value, interest?.interestid))
            .uniq()
            .map((interest) => interest?.interest)
            .join(", ")
            .value()
        : "";
    };

    const handleOptionValues = (field) => {
      return !_.isEmpty(field?.value)
        ? _.chain(field?.options)
            .filter((opt) => _.includes(field?.value, opt?.value))
            .map((opt) => opt?.text)
            .join(", ")
            .value()
        : "";
    };

    const _renderFiles = (field) => {
      const fieldValue = field?.value;
      if (_.isEmpty(fieldValue)) return <></>;
      const files = _.chain(
        typeof fieldValue === "string" ? _.split(fieldValue, ",") : fieldValue
      )
        .map((file) => {
          const fileName = _.join(
            _.initial(
              _.split(_.split(file, "/").pop(), ".").shift().split("_")
            ),
            "_"
          );
          const ext = _.split(file, ".").pop();

          return {
            url: `${CDN_BASE_IMAGES_URL}/${file}`,
            name: `${fileName}.${ext}`,
          };
        })
        .value();

      return (
        <div className="flex flex-col">
          {_.map(files, (file) => {
            return (
              <a
                key={`${field?.id}_file_${file?.name}`}
                className="text-sm text-blue-500 hover:underline"
                href={file?.url}
                target="_blank"
                rel="noreferrer"
              >
                {file?.name}
              </a>
            );
          })}
        </div>
      );
    };

    const _renderHyperLink = (value) => {
      return (
        <a
          href={value}
          target="_blank"
          className="text-sm text-blue-500 hover:underline"
          rel="noreferrer"
        >
          {value}
        </a>
      );
    };

    return (
      <div className="flex flex-col w-full border rounded">
        {_.map(proposalResponse, (field, fieldIndex) => {
          const isAbstractGroup =
            _.isEqual(field?.key, "group") && _.isEqual(field?.id, "abstract");
          const isSingleCheckBox =
            _.isEqual(field?.key, "singlecheckbox") ||
            _.isEqual(field?.id, "termsconditions");

            if (
              isAbstractGroup ||
              isSingleCheckBox ||
              (field?.reviewerVisible !== undefined && !field?.reviewerVisible)
            )
              return null;

          const isInterest =
            _.isEqual(field?.key, "dynamicfield") &&
            _.isEqual(field?.id, "interest");
          const hasOptions = !_.isEmpty(field?.options);
          const isFile = _.isEqual(field?.key, "file");
          const isHyperLink =
            (_.isEqual(field?.key, "hyperlink") ||
              _.includes(
                ["facebook", "linkedin", "instagram", "twitter"],
                field?.id
              )) &&
            (_.includes(field?.value, "https://") ||
              _.includes(field?.value, "http://"));

          return (
            <div
              key={field?.id}
              className={`grid grid-cols-2 w-full${
                !_.isEqual(proposalResponse?.length, fieldIndex + 1)
                  ? " border-b"
                  : ""
              }`}
            >
              <div className="col-span-1 px-2 py-1 text-sm font-medium">
                <span>{field?.label}</span>
                {field?.required && <span className="text-theme">*</span>}
              </div>

              <div className="col-span-1 px-2 py-1 text-sm">
                {isInterest
                  ? handleInterestValue(field?.value)
                  : hasOptions
                  ? handleOptionValues(field)
                  : isFile
                  ? _renderFiles(field)
                  : isHyperLink
                  ? _renderHyperLink(field?.value)
                  : !_.isEmpty(field?.value)
                  ? field?.value
                  : "-"}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const _renderRankSubmission = () => {
    const proposalDetails = commonModal?.data;
    if (
      _.isEmpty(proposalDetails) ||
      _.isEmpty(criteriaList) ||
      (!_.isEmpty(proposalDetails?.markas) &&
        _.isEqual(PROPOSAL_SUBMISSION_TYPES?.types[1], proposalDetails?.markas))
    )
      return <></>;

    const isSubmissionAllowed =
      _.isEmpty(proposalDetails?.markas) &&
      !_.isEqual(REVIEWER_RESPONSE_TYPES.reviewed, proposalDetails?.status);
    let avgScore = 0;
    if (!isSubmissionAllowed) {
      let total = 0;
      let obtained = 0;
      // Total obtained ratings (either out of 5/10)
      _.map(ratedStars, (item) => {
        obtained += item?.value;
      });
      // Total ratings (sum of max)
      _.map(criteriaList, (criteria) => {
        total += _.toNumber(_.split(criteria?.range, "-").pop());
      });
      avgScore = (obtained / total) * 10; // (total obtained / total max) * 10, as avg value is shown out of 10
    }

    const handleStarRating = (
      isAdd = true,
      criteriaId,
      criteriaName = "",
      max = 5,
      value = 0
    ) => {
      if (!isSubmissionAllowed) return;

      let localRatings = _.cloneDeep(ratedStars);

      if (isAdd) {
        let itemPresent = _.find(localRatings, (item) =>
          _.isEqual(item?.criteriaId, criteriaId)
        );
        if (!_.isEmpty(itemPresent)) itemPresent.value = value;
        else localRatings.push({ criteriaId, criteriaName, max, value });
      } else {
        localRatings = _.filter(
          localRatings,
          (item) => !_.isEqual(item?.criteriaId, criteriaId)
        );
      }

      setRatedStars(localRatings);
    };

    return (
      <div className="flex flex-col w-full gap-2">
        <label className="text-sm font-medium">
          {LocalStrings.static.kRankSubmission}
        </label>

        <div className="flex flex-col w-full border rounded">
          {_.map(criteriaList, (criteria) => {
            const range = {
              low: _.toNumber(_.split(criteria?.range, "-").shift()),
              high: _.toNumber(_.split(criteria?.range, "-").pop()),
            };
            const localRating = _.find(
              ratedStars,
              (item) => item?.criteriaId === criteria?.criteriaid
            );
            const list = isSubmissionAllowed ? range?.high : localRating?.value;

            return (
              <div
                key={`criteria_${criteria?.criteriaid}_of_proposal_${criteria?.proposalidfk}`}
                className="grid grid-cols-2 w-full border-b"
              >
                {/* --- Proposal name --- */}
                <div className="col-span-1 px-2 py-1 text-sm font-medium">
                  {criteria?.categoryname}
                  {!isSubmissionAllowed && (
                    <span className="text-theme ml-1">{`(${list})`}</span>
                  )}
                </div>

                {/* - Stars - */}
                <div className="col-span-1 px-2 py-1 text-sm flex gap-1 items-center">
                  {_.times(list, (ea) => {
                    return (
                      <div
                        key={`star_rank_${ea}`}
                        className={`h-6 aspect-square cursor-pointer`}
                        onClick={() =>
                          handleStarRating(
                            true,
                            criteria?.criteriaid,
                            criteria?.categoryname,
                            range?.high,
                            ea + 1
                          )
                        }
                      >
                        {(!localRating || localRating?.value <= ea) &&
                        isSubmissionAllowed ? (
                          <StarOutlinedIcon
                            className="h-full w-full hover:shadow-xl text-theme"
                            color={eventData?.themecolor}
                          />
                        ) : (
                          <StarIcon
                            className="h-full w-full hover:shadow-xl text-theme"
                            color={eventData?.themecolor}
                          />
                        )}
                      </div>
                    );
                  })}

                  {isSubmissionAllowed && (
                    <span
                      className="text-sm text-theme cursor-pointer ml-3"
                      onClick={() =>
                        handleStarRating(false, criteria?.criteriaid)
                      }
                    >
                      {LocalStrings.static.kReset}
                    </span>
                  )}
                </div>
              </div>
            );
          })}

          {/* --- Average rating ---  */}
          {!isSubmissionAllowed && (
            <div className="grid grid-cols-2 w-full border-b">
              {/* --- Proposal name --- */}
              <div className="col-span-1 px-2 py-1 text-sm font-semibold">
                {LocalStrings.static.kAverageRating}
              </div>

              {/* - Stars - */}
              <div className="col-span-1 px-2 py-1 text-sm flex gap-1 items-center">
                <span>{avgScore}</span>
                <div className={`h-6 aspect-square cursor-pointer`}>
                  <StarIcon
                    className="h-full w-full hover:shadow-xl text-theme"
                    color={eventData?.themecolor}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const _renderSubmissionComment = () => {
    const proposalDetails = commonModal?.data;
    if (
      _.isEmpty(proposalDetails) ||
      (!_.isEmpty(proposalDetails?.markas) &&
        _.isEqual(PROPOSAL_SUBMISSION_TYPES?.types[1], proposalDetails?.markas))
    )
      return <></>;
    const isSubmissionAllowed =
      _.isEmpty(proposalDetails?.markas) &&
      !_.isEqual(REVIEWER_RESPONSE_TYPES.reviewed, proposalDetails?.status);

    return (
      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium">
          {LocalStrings.static.kComment}
        </label>

        {isSubmissionAllowed ? (
          renderFieldTextarea({
            input: {
              name: "reviewer-comment",
              onChange: (e) => setReviewerComment(e.target.value),
            },
            meta: {
              touched: !_.isEmpty(reviewerComment),
              error: "",
            },
          })
        ) : (
          <div className="w-full h-18 rounded border text-sm p-3">
            {proposalDetails?.comment}
          </div>
        )}
      </div>
    );
  };

  return (
    <CommonModal className="reviewModal">
      {/* === Modal header === */}
      <CommonModal.header className="w-100 h-12 border-b grid grid-cols-3 p-3">
        <div className="col-span-1 h-full flex justify-start items-center">
          <span className="text-lg font-semibold">
            {LocalStrings.static.kVideAbstract}
          </span>
        </div>

        <div className="col-span-1 h-full w-full flex justify-center items-center">
          {_.isEmpty(commonModal?.data?.markas) &&
            !_.isEqual(
              REVIEWER_RESPONSE_TYPES.reviewed,
              commonModal?.data?.status
            ) && (
              <span
                className={`text-base font-medium animate-pulse text-red-500`}
              >
                {
                  LocalStrings.static[
                    _.isEqual(REVIEW_ALLOWANCES.over, allowedToReview)
                      ? "kReviewTimeOver"
                      : _.isEqual(REVIEW_ALLOWANCES.yet, allowedToReview)
                      ? "kReviewNotStarted"
                      : "kReviewNow"
                  ]
                }
              </span>
            )}
        </div>

        <div className="col-span-1 h-full flex justify-end items-center">
          <img
            className="cursor-pointer"
            src={CrossIcon}
            alt="Close modal"
            onClick={handleCloseModal}
          />
        </div>
      </CommonModal.header>

      {/* === Modal body */}
      <CommonModal.main className="h-reviewContent overflow-y-auto flex flex-col gap-8 p-6">
        {/* ----- Submission status ----- */}
        <div className="flex flex-col gap-3">
          <span className="text-sm font-medium">
            {LocalStrings.static.kSubmissionStatus}
          </span>

          <div className="flex items-center gap-3">
            <div className="bg-white w-max border rounded flex items-center gap-2 px-3 py-2">
              <div
                className={`h-4 aspect-square rounded ${
                  !_.isEmpty(commonModal?.data?.markas) &&
                  _.isEqual(
                    PROPOSAL_SUBMISSION_TYPES.types[0],
                    commonModal?.data?.markas
                  )
                    ? "bg-rejected"
                    : _.isEqual(
                        REVIEWER_RESPONSE_TYPES.reviewed,
                        commonModal?.data?.status
                      )
                    ? "bg-approved"
                    : "bg-unreviewed"
                }`}
              ></div>

              <span>
                {!_.isEmpty(commonModal?.data?.markas) &&
                _.isEqual(
                  PROPOSAL_SUBMISSION_TYPES.types[0],
                  commonModal?.data?.markas
                )
                  ? LocalStrings.static.kRejected
                  : _.isEqual(
                      REVIEWER_RESPONSE_TYPES.reviewed,
                      commonModal?.data?.status
                    )
                  ? LocalStrings.static.kApproved
                  : LocalStrings.static.kUnreviewed}
              </span>
            </div>

            {!_.isEmpty(commonModal?.data?.markas) &&
              _.isEqual(
                PROPOSAL_SUBMISSION_TYPES.types[1],
                commonModal?.data?.markas
              ) && (
                <div className="bg-unreviewed rounded-2xl px-3 py-2 text-xs font-medium italic">
                  {LocalStrings.static.kResubmission}
                </div>
              )}
          </div>
        </div>

        {/* ----- Abstract details url ----- */}
        <div className="flex gap-2">
          <span className="text-sm font-medium">
            {LocalStrings.static.kAbstractDetails}:-
          </span>

          <a
            className="text-sm text-blue-500 hover:underline"
            href={proposalUrl}
            target="_blank"
            rel="noreferrer"
          >
            {proposalUrl}
          </a>
        </div>

        {/* ----- Abstract details ----- */}
        {_renderAbstractDetails()}

        {/* ----- Proposal response ----- */}
        {_renderProposalResponse()}

        {/* ----- Rank submission ----- */}
        {_renderRankSubmission()}

        {/* ----- Reviewer comment ----- */}
        {_renderSubmissionComment()}
      </CommonModal.main>

      {/* === Modal footer === */}
      <CommonModal.footer className="w-100 h-20 border-t flex justify-between items-center p-4">
        {/* ----- Reject - Mark incomplete buttons ----- */}
        <div className="flex gap-3 h-full">
          {!isReviewLayout && _.isEmpty(commonModal?.data?.markas) && (
            <>
              {/* --- Reject button --- */}
              <button
                className="px-3 py-2 bg-rejected hover:bg-red-500 hover:shadow text-sm text-white rounded"
                disabled={
                  !_.isEqual(REVIEW_ALLOWANCES.allowed, allowedToReview) ||
                  (_.isEqual(loader?.type, LOADER_TYPES[0]) && loader.state)
                }
                onClick={() =>
                  handleReviewRejection(PROPOSAL_SUBMISSION_TYPES.types[0])
                }
              >
                {_.isEqual(loader?.type, LOADER_TYPES[0]) && loader.state
                  ? renderSpinner(true)
                  : LocalStrings.static.kReject}
              </button>

              {/* --- Mark incomplete button --- */}
              <button
                className="px-3 py-2 bg-slate-700 hover:bg-slate-800 hover:shadow text-sm text-white rounded"
                disabled={
                  !_.isEqual(REVIEW_ALLOWANCES.allowed, allowedToReview) ||
                  (_.isEqual(loader?.type, LOADER_TYPES[1]) && loader.state)
                }
                onClick={() =>
                  handleReviewRejection(PROPOSAL_SUBMISSION_TYPES.types[1])
                }
              >
                {_.isEqual(loader?.type, LOADER_TYPES[1]) && loader.state
                  ? renderSpinner(true)
                  : LocalStrings.static.kMarkAsInComplete}
              </button>
            </>
          )}
        </div>

        {/* ----- Close - Rate submission button ----- */}
        <div className="flex gap-3 h-full">
          <button
            className="px-3 py-2 bg-white hover:bg-gray-100 hover:shadow text-sm border rounded"
            onClick={handleCloseModal}
          >
            {LocalStrings.static.kClose}
          </button>

          {!isReviewLayout && _.isEmpty(commonModal?.data?.markas) && (
            <button
              className="px-3 py-2 bg-approved hover:bg-emerald-500 hover:shadow text-sm text-white font-medium rounded"
              disabled={
                !_.isEqual(REVIEW_ALLOWANCES.allowed, allowedToReview) ||
                (_.isEqual(loader?.type, LOADER_TYPES[2]) && loader.state)
              }
              onClick={handleResponseSubmission}
            >
              {_.isEqual(loader?.type, LOADER_TYPES[2]) && loader.state
                ? renderSpinner(true)
                : LocalStrings.static.kRateSubmission}
            </button>
          )}
        </div>
      </CommonModal.footer>
    </CommonModal>
  );
};

const mapStateToProps = (state) => {
  let criteriaList = [];
  let criteriaRankList = [];
  let allowedToReview = REVIEW_ALLOWANCES.yet;
  let isReviewLayout = false;

  if (
    !_.isEmpty(state.commonModalReducer?.data) &&
    !_.isEmpty(state.eventData)
  ) {
    const proposalData = state.commonModalReducer?.data;

    criteriaList = !_.isEmpty(state.reviewerAllCriteriaReducer)
      ? _.filter(state.reviewerAllCriteriaReducer, (reviewCriteria) =>
          _.isEqual(reviewCriteria?.proposalidfk, proposalData?.proposalidfk)
        )
      : [];

    criteriaRankList = !_.isEmpty(state.reviewerAllReviewsReducer)
      ? _.filter(state.reviewerAllReviewsReducer, (rankedCriteria) =>
          _.isEqual(rankedCriteria?.responseidfk, proposalData?.id)
        )
      : [];

    // Treat this dates as they are in event time-zone
    const reviewStartDate = new Date(
      proposalData?.proposal_by_proposalidfk?.review_startdate
    );
    const reviewEndDate = new Date(
      proposalData?.proposal_by_proposalidfk?.review_enddate
    );
    const currentTimeInEventTimezone = utcToZonedTime(
      zonedTimeToUtc(new Date(), LOCAL_TIMEZONE),
      state.eventData?.eventtimezone
    );

    if (
      (isAfter(currentTimeInEventTimezone, reviewStartDate) &&
        isBefore(currentTimeInEventTimezone, reviewEndDate)) ||
      isSameDay(currentTimeInEventTimezone, reviewStartDate) ||
      isSameDay(currentTimeInEventTimezone, reviewEndDate)
    )
      allowedToReview = REVIEW_ALLOWANCES.allowed;
    else if (isBefore(currentTimeInEventTimezone, reviewStartDate))
      allowedToReview = REVIEW_ALLOWANCES.yet;
    else if (isAfter(currentTimeInEventTimezone, reviewEndDate))
      allowedToReview = REVIEW_ALLOWANCES.over;

    if (_.isEqual(REVIEWER_RESPONSE_TYPES.reviewed, proposalData?.status)) {
      isReviewLayout = true;
    }
  }

  return {
    session: state.sessionReviewer,
    eventData: state.eventData,
    interestList: state.eventInterestsData,
    commonModal: state.commonModalReducer,
    criteriaList,
    criteriaRankList,
    allowedToReview,
    isReviewLayout,
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    { setCommonModal, rejectProposal, addProposalReview, markResponseReviewed },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(ProposalReview);
